// Vendor Libs
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {
    difference,
    filter,
    get,
    includes,
    isArray,
    isEmpty,
    isObject,
    last,
    lowerCase,
    map,
    remove,
    set,
    some,
    startCase,
    union,
    uniq,
    uniqBy,
} from 'lodash';
import classnames from 'classnames';

// Components
import Checkbox from '../core/checkbox';
import Error from '../core/error';
import ElementTree from '../core/element-tree';
import Loading from '../core/loading';

// Containers
import withFormField from '../../containers/with-form-field';
import withSearch from '../../containers/with-search';

// Contexts
import {FormContext} from '../../contexts/form-context';

// Styles
import './roles-tree.scss';

const servicesDataKey = 'rolesTree';

class RolesTree extends Component {
    getClass() {
        const {className} = this.props;

        return classnames(
            {
                'roles-tree': true,
            },
            className
        );
    }

    getCurrentRoles(formName) {
        const {formData, name} = this.props;

        return get(formData, [formName, 'values', name], []);
    }

    getFieldValue = (formData, name, value) => {
        const formValues = get(formData, ['values', name.substring(0, name.length - 2)]) || [];

        return formValues.includes(value) || includes(this.getAllRoles(formValues), value) ? value : '';
    };

    setFieldValue = (fieldName, inputRoles, {formName, setFieldValue}) => {
        const currentRoles = this.getCurrentRoles(formName);
        let fieldValues = [...inputRoles];
        const childRoles = this.getChildRoles();
        const children = [];

        if (!isArray(inputRoles)) {
            fieldValues = [...currentRoles];

            while (some(fieldValues, (role) => includes(childRoles[role.name], inputRoles))) {
                map(fieldValues, (role) => {
                    if (includes(childRoles[role.name], inputRoles)) {
                        fieldValues = [...fieldValues, ...get(role, 'childRoles', [])];
                        remove(fieldValues, role);
                    }
                });
            }

            const inputRole = this.getRoleByName(inputRoles, currentRoles);

            if (inputRole) {
                remove(fieldValues, inputRole);
                fieldValues = [...fieldValues, ...get(inputRole, 'childRoles', [])];
            }
        }

        map(fieldValues, (name) => {
            if (name in childRoles) {
                children.push(...childRoles[name]);
            }
        });

        fieldValues = filter(
            map(
                uniqBy(fieldValues, (role) => role.name),
                (role) => (isObject(role) ? role : this.getRoleByName(role))
            ),
            (role) => !includes(children, role.name)
        );

        return setFieldValue(fieldName, fieldValues);
    };

    isRoleDisabled = (name) => {
        const {disabledRoles} = this.props;

        return (
            includes(disabledRoles, name) ||
            some(get(this.getChildRoles(), name, []), (role) => includes(disabledRoles, role))
        );
    };

    renderName = (roleName) => {
        const {name} = this.props;
        const parts = roleName.split('_');
        const accessType = lowerCase(last(parts));

        parts.pop();

        const service = lowerCase(parts.join('_'));
        const label = startCase(
            includes(['read', 'update', 'create', 'delete'], accessType)
                ? `${accessType} ${service}`
                : accessType === 'admin'
                  ? `${service} ${accessType}`
                  : roleName
        );

        return (
            <div className="roles-tree--wrapper">
                <Checkbox
                    className="roles-tree--checkbox"
                    disabled={!!this.isRoleDisabled(roleName)}
                    getFieldValue={this.getFieldValue}
                    label={label}
                    name={`${name}[]`}
                    type="checkbox"
                    value={roleName}
                />
            </div>
        );
    };

    getRolesAsTree = (searchData) => {
        const {searchData: searchDataProp} = this.props;
        const rolesAsTree = {};
        const traverse = (obj, root) => {
            if (isArray(obj)) {
                map(obj, (node) => traverse(node, root));
            } else if (isObject(obj)) {
                if (!isEmpty(root)) {
                    set(rolesAsTree, [...root, obj.name], {});
                    traverse(obj.childRoles, [...root, obj.name]);
                } else {
                    traverse(obj.childRoles, [obj.name]);
                }
            }
        };

        traverse(searchData || searchDataProp, []);

        return rolesAsTree;
    };

    getRootRoles = (searchData) => {
        const {searchData: searchDataProp} = this.props;
        const allRoles = [];
        const childRoles = [];
        const traverse = (obj, root) => {
            if (isArray(obj)) {
                map(obj, (node) => traverse(node, root));
            } else if (isObject(obj)) {
                if (!isEmpty(root)) {
                    allRoles.push(obj.name);
                    childRoles.push(obj.name);
                    traverse(obj.childRoles, [...root, obj.name]);
                } else {
                    allRoles.push(obj.name);
                    traverse(obj.childRoles, [obj.name]);
                }
            }
        };

        traverse(searchData || searchDataProp, []);

        return difference(allRoles, childRoles);
    };

    getAllRoles = (searchData) => {
        const {searchData: searchDataProp} = this.props;
        const allRoles = [];
        const traverse = (obj, root) => {
            if (isArray(obj)) {
                map(obj, (node) => traverse(node, root));
            } else if (isObject(obj)) {
                if (!isEmpty(root)) {
                    allRoles.push(obj.name);
                    traverse(obj.childRoles, [...root, obj.name]);
                } else {
                    allRoles.push(obj.name);
                    traverse(obj.childRoles, [obj.name]);
                }
            }
        };

        traverse(searchData || searchDataProp, []);

        return uniq(allRoles);
    };

    getChildRoles = (searchData) => {
        const {searchData: searchDataProp} = this.props;
        const childRoles = {};
        const traverse = (obj, root) => {
            if (isArray(obj)) {
                if (!isEmpty(root)) {
                    map(root, (role) => {
                        if (!(role in childRoles)) {
                            childRoles[role] = [];
                        }
                        map(obj, (node) => {
                            childRoles[role] = union(childRoles[role], [node.name]);
                            return traverse(node, root);
                        });
                    });
                } else {
                    map(obj, (node) => traverse(node, root));
                }
            } else if (isObject(obj)) {
                if (!isEmpty(root)) {
                    traverse(obj.childRoles, [...root, obj.name]);
                } else {
                    traverse(obj.childRoles, [obj.name]);
                }
            }
        };

        traverse(searchData || searchDataProp, []);

        return childRoles;
    };

    getRoleByName = (name, searchData) => {
        const {searchData: searchDataProp} = this.props;
        let role;

        const traverse = (obj) => {
            if (isArray(obj)) {
                map(obj, (node) => traverse(node));
            } else if (isObject(obj)) {
                if (name === obj.name) {
                    role = obj;
                }

                traverse(obj.childRoles);
            }
        };

        traverse(searchData || searchDataProp);

        return role;
    };

    getRoleById = (roleId, searchData) => {
        const {searchData: searchDataProp} = this.props;
        let role;

        const traverse = (obj) => {
            if (isArray(obj)) {
                map(obj, (node) => traverse(node));
            } else if (isObject(obj)) {
                if (roleId === obj.roleId.toString()) {
                    role = obj;
                }

                traverse(obj.childRoles);
            }
        };

        traverse(searchData || searchDataProp);

        return role;
    };

    render() {
        const {error, fetching} = this.props;
        const rolesAsTree = this.getRolesAsTree();
        const rootRoles = this.getRootRoles();

        return !isEmpty(rolesAsTree) ? (
            <FormContext.Consumer>
                {(formContext) => (
                    <FormContext.Provider
                        value={{
                            ...formContext,
                            setFieldValue: (fieldName, value) => this.setFieldValue(fieldName, value, formContext),
                        }}
                    >
                        <div className={this.getClass()}>
                            <Error error={error} />
                            <Loading loading={fetching} />

                            {map(rootRoles, (name) => (
                                <ElementTree
                                    dataTree={rolesAsTree}
                                    elementName={name}
                                    key={name}
                                    path={[]}
                                    renderName={this.renderName}
                                />
                            ))}
                        </div>
                    </FormContext.Provider>
                )}
            </FormContext.Consumer>
        ) : null;
    }
}

RolesTree.propTypes = {
    className: PropTypes.string,
    disabledRoles: PropTypes.array,
    error: PropTypes.string,
    fetching: PropTypes.bool,
    formData: PropTypes.object.isRequired,
    name: PropTypes.string,
    searchData: PropTypes.array.isRequired,
};

export default withSearch(withFormField(RolesTree), servicesDataKey);
